import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Cookie, ImagePlus, Loader2, RotateCcw, Search, Upload } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Snacks() {
  const { user, getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [imageQuery, setImageQuery] = useState("");
  const [imageResults, setImageResults] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [customUrl, setCustomUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [foodishCategories, setFoodishCategories] = useState([]);

  const canEdit = ["admin", "manager"].includes(user?.role);

  useEffect(() => {
    fetchSnacks();
    axios
      .get(`${API_URL}/api/snacks/foodish-categories`, { headers: getAuthHeader() })
      .then((response) => setFoodishCategories(response.data.categories || []))
      .catch(() => setFoodishCategories([]));
  }, []);

  const fetchSnacks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/snacks`, {
        headers: getAuthHeader(),
      });
      setItems(response.data.items || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load snacks");
    } finally {
      setLoading(false);
    }
  };

  const openImageDialog = (item) => {
    setActiveItem(item);
    setImageQuery(item.name);
    setCustomUrl("");
    setImageResults([]);
    setDialogOpen(true);
    searchImages(item.name);
  };

  const searchImages = async (query) => {
    const q = String(query || "").trim();
    if (!q) {
      toast.error("Enter a food name to search images");
      return;
    }
    setImagesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/snacks/image-search`, {
        params: { q },
        headers: getAuthHeader(),
      });
      setImageResults(response.data.images || []);
      if ((response.data.images || []).length === 0) {
        toast.message("No Foodish photos found — try pizza, burger, dessert, or biryani");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Image search failed");
    } finally {
      setImagesLoading(false);
    }
  };

  const saveImage = async (imageUrl) => {
    if (!activeItem) {
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/snacks/${activeItem.id}/image`,
        { image_url: imageUrl },
        { headers: getAuthHeader() }
      );
      toast.success(`Updated image for ${activeItem.name}`);
      setDialogOpen(false);
      fetchSnacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save image");
    } finally {
      setSaving(false);
    }
  };

  const uploadLocalImage = async (file) => {
    if (!activeItem) {
      return;
    }
    if (!file) {
      toast.error("Choose an image file to upload");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(
        `${API_URL}/api/snacks/${activeItem.id}/image/upload`,
        form,
        { headers: getAuthHeader() }
      );
      toast.success(`Uploaded image for ${activeItem.name}`);
      setDialogOpen(false);
      fetchSnacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const resetImage = async (item) => {
    try {
      await axios.delete(`${API_URL}/api/snacks/${item.id}/image`, {
        headers: getAuthHeader(),
      });
      toast.success(`Restored original image for ${item.name}`);
      fetchSnacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No override to reset");
    }
  };

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || item.category_id === categoryFilter;
    return matchesSearch && matchesCategory;
  }).sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-[#561D1A]">Snacks</h1>
        <p className="text-sm text-[#8A5A55] mt-1">
          Update concession photos shown in the chatbot assistant. Menu items come from tickets.westgate; photos can be uploaded from your computer or picked from the{' '}
          <a className="text-[#EC1A23] underline" href="https://github.com/surhud004/Foodish" target="_blank" rel="noreferrer">Foodish</a>
          {' '}API (biryani, burger, pizza, dessert, and more).
        </p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A5A55]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search snacks..."
                className="pl-9"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="all">All categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#8A5A55] gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading snacks...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="py-16 text-center text-[#8A5A55]">
              <Cookie className="h-10 w-10 mx-auto mb-2 opacity-40" />
              No snacks match this filter.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredItems.map((item) => (
                <div key={item.id} className="border rounded-xl overflow-hidden bg-white">
                  <div className="aspect-square bg-[#F7F0E8] flex items-center justify-center overflow-hidden p-2">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-contain" />
                    ) : (
                      <Cookie className="h-10 w-10 text-[#C4B8B0]" />
                    )}
                  </div>
                  <div className="p-3 space-y-2">
                    <p className="text-xs font-extrabold uppercase text-[#561D1A] line-clamp-2 min-h-[2.4em]">{item.name}</p>
                    <p className="text-sm font-bold text-[#EC1A23]">KSh {Number(item.price || 0).toLocaleString()}</p>
                    <p className="text-[11px] text-[#8A5A55]">{item.category_name}{item.image_overridden ? " · custom image" : ""}</p>
                    {canEdit && (
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1" onClick={() => openImageDialog(item)}>
                          <ImagePlus className="h-4 w-4 mr-1" />
                          Image
                        </Button>
                        {item.image_overridden && (
                          <Button size="sm" variant="outline" onClick={() => resetImage(item)}>
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Replace image · {activeItem?.name}</DialogTitle>
          </DialogHeader>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              searchImages(imageQuery);
            }}
          >
            <Input
              value={imageQuery}
              onChange={(e) => setImageQuery(e.target.value)}
              placeholder="Foodish category or snack name (pizza, burger, dessert...)"
            />
            <Button type="submit" disabled={imagesLoading}>
              {imagesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </form>
          {foodishCategories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {foodishCategories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => {
                    setImageQuery(category);
                    searchImages(category);
                  }}
                  className="px-2 py-1 rounded-full text-[11px] font-semibold border border-[#E5E7EB] text-[#561D1A] hover:border-[#EC1A23] hover:text-[#EC1A23]"
                >
                  {category}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-[#8A5A55]">
            Images from Foodish — GET /api/ and GET /api/images/:food (
            <a className="underline text-[#EC1A23]" href="https://github.com/surhud004/Foodish" target="_blank" rel="noreferrer">surhud004/Foodish</a>
            ).
          </p>
          {imagesLoading ? (
            <div className="py-10 text-center text-[#8A5A55]">Searching photos...</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {imageResults.map((image) => (
                <button
                  key={image.url}
                  type="button"
                  disabled={saving || uploading}
                  onClick={() => saveImage(image.url)}
                  className="aspect-square rounded-lg overflow-hidden border hover:border-[#EC1A23]"
                  title={image.label}
                >
                  <img src={image.url} alt={image.label} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2 pt-2 border-t">
            <Label>Upload from your computer</Label>
            <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-background text-sm cursor-pointer hover:border-[#EC1A23]">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span>{uploading ? "Uploading..." : "Choose JPEG, PNG, WEBP, or GIF"}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                disabled={saving || uploading}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  e.target.value = "";
                  if (file) {
                    uploadLocalImage(file);
                  }
                }}
              />
            </label>
          </div>
          <div className="space-y-2">
            <Label>Or paste an https image URL</Label>
            <div className="flex gap-2">
              <Input
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://..."
              />
              <Button
                type="button"
                disabled={saving || uploading || !customUrl}
                onClick={() => saveImage(customUrl)}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
